import { getSetting } from '@myrewards/domain';
import { useQuery } from '@tanstack/react-query';
import { useId, useMemo, useState, type ReactElement } from 'react';
import { settingHistoryQuery } from '../api/settings';
import {
  actorWords,
  HistoryDrawer,
  TextField,
  cx,
  fiscalYearDates,
  fiscalYearOn,
  formatDate,
  formatShortDate,
  type HistoryEntry,
} from '../ui';
import {
  appliesFrom,
  organizationToday,
  stepPoints,
  valueOn,
  type HistoryRow,
  type StepPoint,
} from './model';
import s from './Settings.module.css';

// The History drawer for a setting (PRD §17): who, when, before → after and why, newest first;
// for number settings also the value over time (a step chart) and the value "as of" a date.

const W = 400;
const H = 150;
const PAD = { left: 12, right: 12, top: 26, bottom: 26 };

function dayNumber(date: string): number {
  return Date.UTC(
    Number(date.slice(0, 4)),
    Number(date.slice(5, 7)) - 1,
    Number(date.slice(8, 10)),
  );
}

/** What the chart says before there's a value to draw (APP-093). */
export interface StepChartEmpty {
  /** Says what's true: "No budget for FY2026 yet." */
  headline: string;
  /** When the line will start: "The line starts with the first budget." */
  body: string;
  /** The axis the ghost runs along; the fiscal year today is in by default. */
  from?: string;
  to?: string;
}

/** Any setting's words when nothing has been saved yet (My preferences, values, splits). */
const NOTHING_SAVED: StepChartEmpty = {
  headline: 'Nothing saved yet.',
  body: 'The line starts with the first change, with who made it and when.',
};

/** "Jan 1 2026": a date on the chart's axis. */
const axisDate = (date: string): string => `${formatShortDate(date)} ${date.slice(0, 4)}`;

/**
 * The step chart before its first value: the real axis (the year's first and last days, and today
 * as a dashed line) under a dashed grey staircase that stands in for the line, with a caption that
 * says what's true and when the line starts. The staircase is a pattern, not data.
 */
function GhostStepChart({
  label,
  today,
  empty,
}: {
  label: string;
  today: string;
  empty: StepChartEmpty;
}): ReactElement {
  const year = fiscalYearDates(fiscalYearOn(today));
  const from = empty.from ?? year.from;
  const to = empty.to ?? year.to;
  const start = dayNumber(from);
  const end = Math.max(dayNumber(to), start + 86_400_000);
  const x = (date: string) =>
    PAD.left + ((dayNumber(date) - start) / (end - start)) * (W - PAD.left - PAD.right);
  const xEnd = W - PAD.right;
  const base = H - PAD.bottom;
  const plot = base - PAD.top;
  const at = (share: number) => (PAD.left + share * (xEnd - PAD.left)).toFixed(1);
  const level = (share: number) => (base - share * plot).toFixed(1);
  const ghost = `M${at(0)},${level(0.35)}H${at(0.3)}V${level(0.55)}H${at(0.65)}V${level(0.45)}H${at(1)}`;
  const todayX = x(today);
  const showToday = todayX >= PAD.left && todayX <= xEnd;
  return (
    <figure className={cx(s.chart, s.chartGhost)}>
      <svg
        viewBox={`0 0 ${String(W)} ${String(H)}`}
        role="img"
        aria-label={`${label}, ${formatDate(from)} to ${formatDate(to)}: no line yet.`}
      >
        <line className={s.chartBase} x1={PAD.left} x2={xEnd} y1={base} y2={base} />
        <path className={s.chartGhostStep} d={ghost} />
        {showToday && (
          <g>
            <line className={s.chartToday} x1={todayX} x2={todayX} y1={PAD.top - 8} y2={base} />
            <text className={s.chartNote} x={todayX} y={PAD.top - 12} textAnchor="middle">
              Today
            </text>
          </g>
        )}
        <text className={s.chartNote} x={PAD.left} y={H - 8} textAnchor="start">
          {axisDate(from)}
        </text>
        <text className={s.chartNote} x={xEnd} y={H - 8} textAnchor="end">
          {axisDate(to)}
        </text>
      </svg>
      <figcaption className={s.chartCaption}>
        <span className={s.strong}>{empty.headline}</span> {empty.body}
      </figcaption>
    </figure>
  );
}

/**
 * The value over time: one step per change, each marked, today as a dashed line and the "as of"
 * date as a gold one. A single series, so the title names it and there's no legend; the timeline
 * below is its table view. With nothing to draw yet, a ghost of it on the real axis (APP-093).
 */
export function StepChart({
  points,
  format,
  label,
  today,
  asOf,
  empty = NOTHING_SAVED,
}: {
  points: readonly StepPoint[];
  format: (value: number) => string;
  /** What the chart shows, for screen readers ("FMV per share over time"). */
  label: string;
  today: string;
  asOf?: string | null;
  /** The caption (and axis) when there's no value yet. */
  empty?: StepChartEmpty;
}): ReactElement {
  if (points.length === 0) return <GhostStepChart label={label} today={today} empty={empty} />;
  const first = points[0] as StepPoint;
  const last = points[points.length - 1] as StepPoint;
  const start = dayNumber(first.date);
  const lastDay = Math.max(dayNumber(last.date), dayNumber(today), asOf ? dayNumber(asOf) : 0);
  // Room after the last change so its step reads as a level, not a point.
  const end = lastDay + Math.max(30 * 86_400_000, (lastDay - start) * 0.08);
  const max = Math.max(...points.map((p) => p.value));
  const x = (date: string) =>
    PAD.left + ((dayNumber(date) - start) / (end - start || 1)) * (W - PAD.left - PAD.right);
  const xEnd = W - PAD.right;
  const y = (v: number) => H - PAD.bottom - (v / (max * 1.15 || 1)) * (H - PAD.top - PAD.bottom);

  let d = '';
  points.forEach((p, i) => {
    const px = x(p.date);
    const py = y(p.value);
    if (i === 0) d += `M${px.toFixed(1)},${py.toFixed(1)}`;
    else d += `H${px.toFixed(1)}V${py.toFixed(1)}`;
  });
  d += `H${xEnd.toFixed(1)}`;

  const summary = `${label}: ${points
    .map(
      (p) =>
        `${format(p.value)} from ${formatDate(p.date)}${p.note === undefined ? '' : `, ${p.note}`}`,
    )
    .join('; ')}.`;
  const todayX = x(today);
  const asOfX = asOf ? x(asOf) : null;

  return (
    <figure className={s.chart}>
      <svg viewBox={`0 0 ${String(W)} ${String(H)}`} role="img" aria-label={summary}>
        <line
          className={s.chartBase}
          x1={PAD.left}
          x2={xEnd}
          y1={H - PAD.bottom}
          y2={H - PAD.bottom}
        />
        {todayX <= xEnd && (
          <g>
            <line
              className={s.chartToday}
              x1={todayX}
              x2={todayX}
              y1={PAD.top - 8}
              y2={H - PAD.bottom}
            />
            <text className={s.chartNote} x={todayX} y={H - 8} textAnchor="middle">
              Today
            </text>
          </g>
        )}
        {asOfX !== null && asOfX <= xEnd && (
          <line
            className={s.chartAsOf}
            x1={asOfX}
            x2={asOfX}
            y1={PAD.top - 8}
            y2={H - PAD.bottom}
          />
        )}
        <path className={s.chartStep} d={d} />
        {points.map((p, i) => {
          const isLast = i === points.length - 1;
          return (
            <g key={p.date}>
              <title>{`${format(p.value)} from ${formatDate(p.date)}${p.upcoming ? ' (ahead)' : ''}${p.note === undefined ? '' : `, ${p.note}`}`}</title>
              <circle
                className={cx(
                  s.chartMark,
                  isLast && s.chartMarkLast,
                  p.upcoming && s.chartMarkAhead,
                )}
                cx={x(p.date)}
                cy={y(p.value)}
                r={5}
              />
              {(i === 0 || isLast) && (
                <text
                  className={s.chartValue}
                  x={x(p.date)}
                  y={y(p.value) - 10}
                  textAnchor={i === 0 && points.length > 1 ? 'start' : 'middle'}
                >
                  {format(p.value)}
                </text>
              )}
            </g>
          );
        })}
        <text className={s.chartNote} x={PAD.left} y={H - 8} textAnchor="start">
          {axisDate(first.date)}
        </text>
      </svg>
    </figure>
  );
}

/** Pick a date to see the value that applied then (PRD §17 "As of a date"). */
export function AsOf({
  rows,
  format,
  date,
  onDate,
}: {
  rows: readonly HistoryRow[];
  format: (value: unknown) => string;
  date: string;
  onDate: (date: string) => void;
}): ReactElement {
  const found = /^\d{4}-\d{2}-\d{2}$/.test(date) ? valueOn(rows, date) : null;
  const resultId = useId();
  return (
    <div className={s.asOf}>
      <TextField
        label="Value as of"
        type="date"
        value={date}
        onChange={onDate}
        aria-describedby={resultId}
      />
      <p id={resultId} className={s.asOfResult} aria-live="polite">
        {!/^\d{4}-\d{2}-\d{2}$/.test(date)
          ? 'Pick a date to see the value that applied then.'
          : found === null
            ? `Nothing was set yet on ${formatDate(date)}.`
            : `On ${formatDate(date)}: ${format(found.value)}, in effect from ${formatDate(appliesFrom(found))}.`}
      </p>
    </div>
  );
}

export interface SettingHistoryDrawerProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  settingKey: string;
  scope?: string;
  /** The value's name ("FMV per share"). */
  title: string;
  /** What it belongs to ("Values · Areté stock"). */
  subject: string;
  format: (value: unknown) => string;
  /** Number settings get the step chart and "as of". */
  numeric?: (value: unknown) => number | null;
  /** Formats a chart value (defaults to `format`). */
  formatNumber?: (value: number) => string;
  /**
   * A list kept in a table (requesters, Sector Leads): each entry is one person added or removed,
   * so there's no "before" value and nothing "in effect".
   */
  listChanges?: boolean;
  /** The chart's caption before the first value ("No FMV yet."); a general one by default. */
  emptyChart?: StepChartEmpty;
}

/**
 * What a setting was before its first saved change: its built-in default, named as one ("Off
 * (default)"), or "Not set before" when it has none. Never a bare fallback.
 */
function firstBefore(key: string, format: (value: unknown) => string): string {
  const fallback = getSetting(key)?.default;
  if (fallback === undefined || fallback === null) return 'Not set before';
  const words = format(fallback);
  return words === 'Not set' ? 'Not set before' : `${words} (default)`;
}

/** The History drawer for one setting, with the value over time when it's a number. */
export function SettingHistoryDrawer({
  isOpen,
  onOpenChange,
  settingKey,
  scope = 'system',
  title,
  subject,
  format,
  numeric,
  formatNumber,
  listChanges = false,
  emptyChart,
}: SettingHistoryDrawerProps): ReactElement {
  const today = organizationToday();
  const q = useQuery({ ...settingHistoryQuery(settingKey, scope), enabled: isOpen });
  const rows = useMemo(() => q.data?.entries ?? [], [q.data]);
  const [asOf, setAsOf] = useState(today);

  const entries = useMemo<HistoryEntry[]>(
    () =>
      rows.map((row, i) => {
        const older = rows[i + 1];
        return {
          id: row.id,
          at: row.recordedAt,
          actor: { name: actorWords(row.by?.name ?? 'MyRewards setup', row.previewedBy?.name) },
          ...(!listChanges && {
            before: older === undefined ? firstBefore(settingKey, format) : format(older.value),
          }),
          after:
            row.effectiveFrom === null
              ? format(row.value)
              : `${format(row.value)} from ${formatDate(row.effectiveFrom)}`,
          reason: row.reason,
        };
      }),
    [rows, format, listChanges, settingKey],
  );

  const inForce = listChanges ? null : valueOn(rows, today);
  const points = numeric === undefined ? [] : stepPoints(rows, numeric, today);
  const fmtNumber = formatNumber ?? ((v: number) => format(v));

  return (
    <HistoryDrawer
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      subject={subject}
      title={title}
      loading={q.isPending}
      {...(inForce !== null && {
        current: { label: 'In effect today', value: format(inForce.value) },
      })}
      entries={entries}
      overview={
        // Once the history has loaded, a number setting always shows its chart: a ghost of it
        // until the first value (APP-093), never a heading over nothing.
        numeric !== undefined && q.isSuccess ? (
          <div className={s.overview}>
            <h3 className={cx('eyebrow', s.sectionLabel)}>Value over time</h3>
            <StepChart
              points={points}
              format={fmtNumber}
              label={`${title} over time`}
              today={today}
              asOf={asOf}
              {...(emptyChart !== undefined && { empty: emptyChart })}
            />
            <AsOf rows={rows} format={format} date={asOf} onDate={setAsOf} />
          </div>
        ) : undefined
      }
    />
  );
}
